import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { invokeEdgeFunction } from '@/lib/edgeFunctionClient';
import { useAuth } from '@/lib/auth';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';

/**
 * Validates UUID format to prevent database errors
 * @param str - String to validate as UUID
 * @returns True if string is a valid UUID v4 format
 */
const isValidUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

/**
 * Hook for coordinating payment setup between client and vendor
 * 
 * This hook manages the payment setup workflow after a job is awarded:
 * - Tracks whether client has set up Stripe Checkout
 * - Tracks whether vendor has set up Stripe Connect
 * - Subscribes to real-time updates from both parties
 * - Auto-redirects to milestone planning when both parties complete setup
 * - Displays toast notifications when partner completes their setup
 * 
 * Payment setup must be completed by both parties before work can begin on a job.
 * 
 * @param jobId - The UUID of the job (not the public_id)
 * @param jobPublicId - The public identifier for the job (for navigation)
 * @returns Object with payment status, loading state, user role, and refetch function
 * 
 * @example
 * ```typescript
 * const { paymentStatus, loading, userRole } = usePaymentSetupCoordination(jobId, jobPublicId);
 * 
 * if (paymentStatus?.both_complete) {
 *   console.log('Both parties have completed payment setup');
 * } else if (userRole === 'client' && !paymentStatus?.client_setup_complete) {
 *   console.log('Waiting for client to complete Stripe Checkout setup');
 * } else if (userRole === 'vendor' && !paymentStatus?.vendor_setup_complete) {
 *   console.log('Waiting for vendor to complete Stripe Connect setup');
 * }
 * ```
 */
export const usePaymentSetupCoordination = (jobId: string, jobPublicId: string, jobStatus?: string) => {
  const [paymentStatus, setPaymentStatus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);
  const [hasRetried, setHasRetried] = useState(false);
  const [userRole, setUserRole] = useState<'client' | 'vendor'>('client');
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Fetch payment setup status
  const fetchStatus = async () => {
    if (!jobId) {
      // Keep loading=true if jobId is missing; the page will handle the "not found" state
      return;
    }

    // Validate UUID to prevent database errors
    if (!isValidUUID(jobId)) {
      console.error('Invalid UUID passed to usePaymentSetupCoordination:', jobId);
      // Keep loading=true if jobId is invalid; don't show stale "incomplete" state
      return;
    }

    // CRITICAL: Skip fetching for non-awarded jobs to prevent 406 errors
    if (!jobStatus || !['awarded', 'in_progress', 'completed_pending_review', 'rejected', 'accepted'].includes(jobStatus)) {
      setLoading(false);
      setHasFetched(true);
      return;
    }

    // CRITICAL: Wait for user.organization_id to be available before proceeding
    if (!user?.organization_id) {
      console.log('[PaymentSetupCoordination] Waiting for user organization data...');
      setLoading(true);
      return;
    }

    try {
      // Use maybeSingle() to gracefully handle:
      // 1. No row found (returns null data, no error)
      // 2. User not authorized via RLS (returns null data, no error)
      // This prevents infinite retry loops when user can view job but isn't client/vendor
      const { data, error } = await supabase
        .from('payment_setup_status')
        .select('*')
        .eq('job_id', jobId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching payment setup status:', error);
        setLoading(false);
        setHasFetched(true);
        return;
      }

      // If no data returned (no row or not authorized), handle gracefully
      if (!data && !hasRetried) {
        console.log('[PaymentSetupCoordination] No payment status found, scheduling one-time retry...');
        setHasRetried(true);
        setTimeout(() => {
          fetchStatus();
        }, 800);
        return; // Keep loading=true until retry completes
      }

      setPaymentStatus(data);

      // Determine user role - check vendor FIRST since vendors need to complete Stripe Connect
      if (data && user?.organization_id) {
        // Detect invalid same-org state
        if (data.client_org_id === data.vendor_org_id) {
          console.warn('[PaymentSetupCoordination] Invalid state: client and vendor are same organization', {
            org_id: data.client_org_id,
            job_id: data.job_id
          });
        }

        // Check vendor role FIRST (priority for Stripe Connect setup)
        if (data.vendor_org_id === user.organization_id) {
          setUserRole('vendor');
          console.log('[PaymentSetupCoordination] User role: vendor', {
            user_org: user.organization_id,
            vendor_org: data.vendor_org_id
          });
        }
        // Then check client role
        else if (data.client_org_id === user.organization_id) {
          setUserRole('client');
          console.log('[PaymentSetupCoordination] User role: client', {
            user_org: user.organization_id,
            client_org: data.client_org_id
          });
        }
        // Handle case where user org matches neither (shouldn't happen)
        else {
          console.error('[PaymentSetupCoordination] User org matches neither client nor vendor', {
            user_org: user.organization_id,
            client_org: data.client_org_id,
            vendor_org: data.vendor_org_id
          });
          setUserRole('client'); // Safe default
        }
      }
    } catch (error) {
      console.error('Error in fetchStatus:', error);
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, [jobId, user?.organization_id]);

  // Auto-repair if payment setup record is missing
  useEffect(() => {
    const initializeIfMissing = async () => {
      // Only attempt initialization if fetch has completed and no record exists
      if (!hasFetched || loading || paymentStatus !== null) return;

      // CRITICAL: Skip initialization for draft jobs or jobs without awards
      if (!jobStatus || !['awarded', 'in_progress', 'completed_pending_review', 'rejected', 'accepted'].includes(jobStatus)) {
        console.log('[PaymentSetupCoordination] Skipping initialization - job not awarded yet:', { jobStatus });
        return;
      }

      // CRITICAL: Validate jobId before proceeding to prevent empty string errors
      if (!jobId || !isValidUUID(jobId)) {
        console.warn('[PaymentSetupCoordination] Skipping initialization - invalid jobId:', jobId);
        return;
      }

      console.warn('[PaymentSetupCoordination] No payment setup record found, initializing...', {
        job_id: jobId
      });

      // silently initialize without toasts to avoid noise in staging

      try {
        const { error } = await invokeEdgeFunction('init-payment-setup', {
          body: { jobId },
          callerOperation: 'usePaymentSetupCoordination.autoRepairMissing',
        });

        if (error) {
          console.error('[PaymentSetupCoordination] Initialization failed:', error);
          toast({
            title: "Initialization failed",
            description: "Could not create payment setup record",
            variant: "destructive"
          });
        } else {
          console.log('[PaymentSetupCoordination] Initialization successful, refetching...');
          setTimeout(() => {
            fetchStatus();
            // silent success; refetch after a short delay
          }, 1000);
        }
      } catch (error) {
        console.error('[PaymentSetupCoordination] Initialization error:', error);
      }
    };

    initializeIfMissing();
  }, [hasFetched, loading, paymentStatus, jobId, jobStatus]);

  // Auto-repair if invalid same-org state detected
  useEffect(() => {
    const repairIfNeeded = async () => {
      if (paymentStatus && 
          paymentStatus.client_org_id === paymentStatus.vendor_org_id) {
        console.warn('[PaymentSetupCoordination] Detected invalid same-org state, triggering repair...', {
          job_id: paymentStatus.job_id,
          org_id: paymentStatus.client_org_id
        });

        // silent repair - avoid noisy toasts

        try {
          // Re-invoke init-payment-setup to fix the data
          const { error } = await invokeEdgeFunction('init-payment-setup', {
            body: { jobId },
            callerOperation: 'usePaymentSetupCoordination.autoRepairSameOrg',
          });

          if (error) {
            console.error('[PaymentSetupCoordination] Repair failed:', error);
            toast({
              title: "Repair failed",
              description: "Could not correct payment setup data",
              variant: "destructive"
            });
          } else {
            console.log('[PaymentSetupCoordination] Repair successful, refetching...');
            // Wait a moment for the database to update
            setTimeout(() => {
              fetchStatus();
                // silent correction confirmation
            }, 1000);
          }
        } catch (error) {
          console.error('[PaymentSetupCoordination] Repair error:', error);
        }
      }
    };

    repairIfNeeded();
  }, [paymentStatus, jobId]);

  // Subscribe to realtime updates
  // Listens for changes to the payment_setup_status table and updates local state
  // Shows toast notifications when the partner completes their setup
  // Auto-redirects to milestones page when both parties complete setup
  useEffect(() => {
    if (!jobId || !isValidUUID(jobId)) return;

    const handleRealtimeChange = (payload: any) => {
      console.log('Payment setup status changed:', payload);
      const newData = payload.new;
      
      setPaymentStatus((prev: any) => {
        // Show toast if partner completed
        if (prev && !prev.client_setup_complete && newData.client_setup_complete && userRole === 'vendor') {
          toast({
            title: 'Client Setup Complete',
            description: 'The client has completed payment setup',
          });
        } else if (prev && !prev.vendor_setup_complete && newData.vendor_setup_complete && userRole === 'client') {
          toast({
            title: 'Vendor Setup Complete',
            description: 'The vendor has completed Stripe Connect setup',
          });
        }

        // Auto-redirect when both complete
        if (newData.both_complete && (!prev || !prev.both_complete)) {
          toast({
            title: 'Payment Setup Complete!',
            description: 'Redirecting to milestone planning...',
          });

          setTimeout(() => {
            navigate(`/jobs/${jobPublicId}/milestones`);
          }, 2500);
        }

        return newData;
      });
    };

    const channel = supabase
      .channel('payment-setup-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'payment_setup_status',
          filter: `job_id=eq.${jobId}`
        },
        handleRealtimeChange
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'payment_setup_status',
          filter: `job_id=eq.${jobId}`
        },
        handleRealtimeChange
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [jobId, jobPublicId, userRole, navigate, toast]);

  return {
    paymentStatus,
    loading,
    userRole,
    refetch: fetchStatus,
  };
};